import { useEffect, ReactNode } from "react";
import {
  ArrowDownLeft,
  ArrowUpRight,
  BadgeIndianRupee,
  ChevronRight,
  Gift,
  Ticket,
  UsersRound,
  WalletCards,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { WalletBalanceResponse } from "../../../Redux/wallet_apis/types";
import {
  getAllInfinityMembers,
  getMyReferrals,
} from "../../../Redux/Teams/slice";
import { AppDispatch, RootState } from "../../../store";

interface DashboardListCardsProps {
  balance?: WalletBalanceResponse["data"] | null;
}

interface ActivityCard {
  id: string;
  title: string;
  value: ReactNode;
  supportingText: string;
  footerLabel: string;
  footerValue: ReactNode;
  icon: ReactNode;
}

const money = (value?: number) => `₹${(value || 0).toFixed(2)}`;

const DashboardListCards = ({ balance }: DashboardListCardsProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const { totalreferralMembers, infinityMembersPagination } = useSelector(
    (state: RootState) => state.teams,
  );

  useEffect(() => {
    dispatch(getAllInfinityMembers({ page: 1 }));
  }, [dispatch]);

  useEffect(() => {
    dispatch(getMyReferrals());
  }, [dispatch]);

  const cards: ActivityCard[] = [
    {
      id: "samriddhi",
      title: "My Salar Samriddhi",
      value: infinityMembersPagination?.totalMembers || 0,
      supportingText: "Active community participation",
      footerLabel: "My Invitations",
      footerValue: totalreferralMembers || 0,
      icon: <UsersRound size={20} />,
    },
    {
      id: "payout",
      title: "My Payouts",
      value: money(balance?.stats?.totalWithdrawn),
      supportingText: "Processed payout amount",
      footerLabel: "Pending Payouts",
      footerValue: money(balance?.pendingBalance),
      icon: <BadgeIndianRupee size={20} />,
    },
    {
      id: "credited",
      title: "Credited",
      value: money(balance?.stats?.totalTransferredIn),
      supportingText: "Total credited amount",
      footerLabel: "Debited",
      footerValue: money(balance?.stats?.totalTransferredOut),
      icon: <ArrowDownLeft size={20} />,
    },
    {
      id: "funds",
      title: "Funds Received",
      value: money(balance?.funds),
      supportingText: "Funds available in account",
      footerLabel: "Transferred",
      footerValue: money(balance?.stats?.totalTransferredOut),
      icon: <ArrowUpRight size={20} />,
    },
    {
      id: "voucher",
      title: "Shopping Voucher",
      value: <>₹{balance?.paymentBalances?.shoppingVoucher || 0}</>,
      supportingText: "Available voucher value",
      footerLabel: "Salar Ruby",
      footerValue: 0,
      icon: <WalletCards size={20} />,
    },
    {
      id: "lucky",
      title: "Lucky Draw",
      value: 0,
      supportingText: "Eligible lucky draw entries",
      footerLabel: "Tickets",
      footerValue: (
        <>
          <Ticket size={13} aria-hidden="true" /> 0
        </>
      ),
      icon: <Gift size={20} />,
    },
  ];

  return (
    <section className="user-dashboard-activity-section">
      <div className="user-dashboard-section-heading">
        <div>
          <p className="user-dashboard-overline">ACCOUNT ACTIVITY</p>
          <h2>Your balances & benefits</h2>
          <p>Quick access to your most important account values.</p>
        </div>
      </div>

      <div className="user-dashboard-activity-grid">
        {cards.map((stat) => (
          <article
            className="user-dashboard-activity-card"
            key={stat.id}
            data-stat={stat.id}
          >
            <div className="user-dashboard-activity-icon">{stat.icon}</div>
            <div className="user-dashboard-activity-copy">
              <span>{stat.title}</span>
              <strong>{stat.value}</strong>
              <small>{stat.supportingText}</small>
            </div>
            <div className="user-dashboard-activity-side">
              <span>{stat.footerLabel}</span>
              <strong>{stat.footerValue}</strong>
            </div>
            <ChevronRight
              className="user-dashboard-activity-arrow"
              size={18}
            />
          </article>
        ))}
      </div>
    </section>
  );
};

export default DashboardListCards;
